import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import noop from '../utils/noop';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import CloseBigIcon from '@wix/da-ds/pkg/Icons/CloseBig';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { ResourceType } from '../types/resource';
import { WatchSuggestionType } from './redux/types';
import UserSampleGallery from './UserSampleGallery';
import {
  BiData,
  WatchSuggestionRemoveClickBiEvent,
} from '@wix/da-bi/pkg/events';

import s from './WatchSuggestionList.scss';

export interface Props {
  watchSuggestions: WatchSuggestionType[];
  /** limits number shown */
  numSuggestionsShown?: number;
  currentUserId?: number;
  /** the 'x' in 'back to x' when showing a user's gallery deviation in duperbrowse */
  duperbrowseContextTitle?: string;
  className?: string;
  /** should allow unsuggesting/removing a user from the list? */
  allowUnsuggest?: boolean;
  /** hits api to perm remove the suggestion for given user */
  unsuggestUser?: (userId: number) => void;
  /** fired when any of the suggested users are watched */
  onWatch?: () => void;
}

// todo: could be moved into browse now
export const WatchSuggestionList: React.FC<React.PropsWithChildren<Props>> = ({
  watchSuggestions,
  numSuggestionsShown,
  currentUserId,
  duperbrowseContextTitle,
  className,
  allowUnsuggest,
  unsuggestUser = noop,
  onWatch = noop,
}) => {
  const { t } = useTranslation();
  const [hiddenUserIds, setHiddenUserIds] = useState([] as number[]);
  const removeAndUnsuggestUser = useCallback(
    userId => {
      unsuggestUser(userId);
      setHiddenUserIds([...hiddenUserIds, userId]);
    },
    [hiddenUserIds, setHiddenUserIds, unsuggestUser]
  );

  const toBeRendered = watchSuggestions
    .map((suggestion, i) => ({ ...suggestion, rank: i }))
    .filter(({ user }) => hiddenUserIds.indexOf(user.userId) < 0)
    .slice(0, numSuggestionsShown);

  return (
    <ul className={className}>
      {toBeRendered.length ? (
        toBeRendered.map(({ user, deviations, rank }) => (
          <li key={user.useridUuid} className={s['list-item']}>
            <BiLoggerContextProvider value={{ rank }}>
              <UserSampleGallery
                className={s['user-sample-gallery']}
                user={user}
                deviations={deviations}
                // this... shouldn't happen...
                isOwnUser={user.userId === currentUserId}
                duperbrowseContextTitle={duperbrowseContextTitle}
                onWatch={onWatch}
              />
              {allowUnsuggest && (
                <IconButton
                  className={s['unsuggest-button']}
                  size="small"
                  icon={CloseBigIcon}
                  onClick={() => removeAndUnsuggestUser(user.userId)}
                  aria-label={t('watchSuggestionsV2.a11y.unsuggest')}
                  biData={BiData<WatchSuggestionRemoveClickBiEvent>({
                    evid: 406,
                    sectionname: 'watch_suggestions',
                    typeid: ResourceType.USER,
                    itemid: user.userId,
                    items_selected_count: 1,
                  })}
                />
              )}
            </BiLoggerContextProvider>
          </li>
        ))
      ) : (
        <p className={s['empty']}>{t('watchSuggestionsV2.empty')}</p>
      )}
    </ul>
  );
};
WatchSuggestionList.displayName = 'WatchSuggestionList';
export default WatchSuggestionList;
